<template>
  <b-form-group class="accordion" role="tablist">
    <b-card no-body class="mb-1">
      <b-card-header header-tag="header" role="tab">
        <div style="cursor: pointer" @click="visible = !visible">SIMULATION DELIVERY</div>
      </b-card-header>
      <b-collapse v-model="visible" role="tabpanel">
        <b-card-body>
          <VForm ref="observer" v-slot="{ meta: formMeta }" as="div">
            <b-form @submit.prevent="saveForm">
              <div v-if="hasAdministeredDoses">
                <input
                  id="custom-start"
                  v-model="form.customStart"
                  class="form-check-input"
                  type="checkbox"
                  @input="customStartChange"
                />
                <label for="custom-start" class="form-check-label">Custom Simulation Start</label>
                <div v-if="form.customStart">
                  <p class="text-lg text-black font-semibold">
                    Enter date and time for simulation start
                  </p>
                  <DateTimeInput :field="fieldName" @datetime-event="simulationDateTimeValue" />
                </div>
                <div v-if="customStartError" style="color: #dc3545">{{ customStartError }}</div>
              </div>
              <hr />
              <b-input-group>
                <label>Days to display</label>
                <VField
                  v-slot="{ meta }"
                  v-model="form.daysToDisplay"
                  name="DaysToDisplay"
                  :rules="rulesDaysToDisplay"
                >
                  <b-form-input
                    id="DaysToDisplay"
                    v-model="form.daysToDisplay"
                    type="number"
                    step="1"
                    :number="true"
                    placeholder="Days to display"
                    :state="getValidationState(meta)"
                    @input="saveForm"
                  />
                </VField>
              </b-input-group>
              <VErrorMessage name="DaysToDisplay" as="div" class="invalid-feedback d-block" />
              <div>
                <input
                  id="switch-variability"
                  v-model="form.applyInterindividualVariability"
                  class="form-check-input"
                  type="checkbox"
                />
                <label for="switch-variability" class="form-check-label"
                  >Include interindividual variability</label
                >
              </div>

              <span class="flex align-content-center">
                <b-button
                  class="rounded-circle shadow bg-secondary"
                  title="Add target"
                  :disabled="!formMeta.valid || !isFormValid"
                  @click="saveForm"
                  ><PlusIcon></PlusIcon
                ></b-button>
              </span>
            </b-form>
          </VForm>
        </b-card-body>
      </b-collapse>
    </b-card>
  </b-form-group>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { isValidDateValue } from '@/models/utils/datetime';
import PlusIcon from 'vue-material-design-icons/Plus.vue';
import DateTimeInput from '@/components/inputs/DateTimeInput.vue';
import { useDosingStore } from '@/stores/dosing';

defineOptions({ name: 'DeliveryInput' });

const props = defineProps<{ deliveryData?: any }>();

const store = useDosingStore();
const visible = ref(false);
const observer = ref<any>(null);
const fieldName = 'simulation_start';
const customStartError = ref<string | null>(null);
const rulesDaysToDisplay = { between: [2, 7] };

function initForm() {
  const delivery = props.deliveryData ?? null;
  return {
    method: delivery ? delivery.method : 'IV',
    duration: delivery ? delivery.duration : 1,
    durationUnit: delivery ? delivery.durationUnit : 'hours',
    daysToDisplay: delivery ? delivery.daysToDisplay : 3,
    customStart: delivery && delivery.startDT,
    startDT: delivery ? delivery.startDT : (null as Date | null),
    applyInterindividualVariability: delivery?.applyInterindividualVariability ?? true,
  };
}

const form = ref(initForm());

watch(
  () => props.deliveryData,
  () => {
    form.value = initForm();
  },
);

const hasAdministeredDoses = computed(() => {
  const simulation = store.getSimulation;
  return simulation && simulation.hasAdministeredDoses();
});

const isFormValid = computed(() => customStartError.value == null);

function saveForm() {
  observer.value.validate().then(({ valid }: { valid: boolean }) => {
    if (valid) store.saveDelivery(form.value);
  });
}

function getValidationState(meta: { validated: boolean; valid: boolean }): boolean | null {
  return meta.validated ? meta.valid : null;
}

function simulationDateTimeValue(event: { field: string | null; value: Date }) {
  if (event.field === fieldName) {
    if (!isValidDateValue(event.value)) {
      // Don't keep a start the user has since changed; null means the default start
      form.value.startDT = null;
      customStartError.value = 'Enter a valid date and time';
      return;
    }
    const sim = store.getSimulation;
    const minStart = sim ? sim.minimumSimulationStart() : null;
    if (minStart && event.value && minStart.getTime() > event.value.getTime()) {
      customStartError.value = `Simulation can only start after last administered dose ${minStart}`;
      return;
    }
    customStartError.value = null;
    form.value.startDT = event.value;
  }
}

function customStartChange(event: any) {
  if (!event.value) {
    form.value.startDT = null;
    customStartError.value = null;
  }
}
</script>

<style scoped></style>
